import React, { useCallback, useEffect, useRef, useState } from "react";
//@ts-ignore
import { filterIcon } from "@lululemon/ui-icons";
import { Waypoint } from "react-waypoint";

// Atoms
import { Pill, PILL_ICON_POSITION } from "../../atoms";
// Molecules
import { FilterPills } from "../../molecules";

// Styles
import styles from "./PopularStickyFilters.module.scss";

/**
 * `PopularStickyFilters` is a component that displays popular filters in a sticky container.
 *
 * @component
 * @param {Object} props - The properties that define the component.
 * @param {Facet[]} props.filters - The filters to be displayed.
 * @param {number} props.appliedFiltersCount - The number of filters that have been applied.
 * @param {Function} props.onMobileSortAndFilterBtnClick - The function to be called when the sort and filter button is clicked on mobile.
 * @param {Function} props.onFilterPillClick - The function to be called when a filter pill is clicked.
 *
 * @example
 * <PopularStickyFilters
 *   filters={filters}
 *   appliedFiltersCount={2}
 *   onMobileSortAndFilterBtnClick={() => {}}
 *   onFilterPillClick={(name) => {}}
 * />
 *
 * @returns {ReactElement} The `PopularStickyFilters` component.
 */

interface PopularStickyFiltersProps {
  filters: Facet[];
  appliedFiltersCount: number;
  onMobileSortAndFilterBtnClick: (trackEventObj: TrackEventObj) => void;
  onFilterPillClick: (name: string, trackEventObj: TrackEventObj) => void;
}

export const PopularStickyFilters: React.FC<PopularStickyFiltersProps> = ({
  filters,
  appliedFiltersCount,
  onMobileSortAndFilterBtnClick,
  onFilterPillClick,
}) => {
  const [isSticky, setIsSticky] = useState(false);

  const handleStickyFilterVisibility = useCallback(
    (showSticky: boolean) => setIsSticky(showSticky),
    [setIsSticky]
  );

  const handleFilterPillClick = useCallback(
    (name: string, trackEventObj: TrackEventObj) => {
      const cleanedName = name?.replace(/ /g, "-");
      onFilterPillClick?.(cleanedName, trackEventObj);
    },
    [onFilterPillClick]
  );

  return (
    <div
      className={styles.popularStickyFiltersWrapper}
      data-testid="popular-sticky-filters"
    >
      <Waypoint
        onEnter={() => handleStickyFilterVisibility(false)}
        onLeave={() => handleStickyFilterVisibility(true)}
      />
      <div
        className={`${styles.popularStickyFiltersWrapper_filter} ${
          isSticky
            ? styles.popularStickyFiltersWrapper_isSticky
            : styles.popularStickyFiltersWrapper_isNotSticky
        }`}
      >
        <div className={styles.popularStickyFiltersWrapper_bttn}>
          <Pill
            icon={filterIcon}
            iconPosition={PILL_ICON_POSITION.BEFORE}
            onClick={onMobileSortAndFilterBtnClick}
            text="Filter & Sort"
            selected={appliedFiltersCount > 0}
            count={appliedFiltersCount}
          />
        </div>

        <FilterPills filters={filters} onClick={handleFilterPillClick} />
      </div>
    </div>
  );
};

PopularStickyFilters.displayName = "PopularStickyFilters";
